import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Button,
  Modal,
  ListingTypeModal,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import TopbarSearchMenu from '../TopbarSearchMenu/TopbarSearchMenu';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import CartOutlineIcon from '../../../../components/IconComponent/CartIcon';
import HeartOutlineIcon from '../../../../components/IconComponent/HeartOutlineIcon';

const SignupLink = () => {
  return (
    <NamedLink id="signup-link" name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink id="login-link" name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      id="inbox-link"
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: inboxTab }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

export const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, intl, avatarClassName }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="profile-menu-label"
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.profileMenu' })}
      >
        <Avatar className={classNames(css.avatar, avatarClassName)} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    onManageDisableScrolling,
    wishlistCount,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [isListingTypeModalOpen, setIsListingTypeModalOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      intl={intl}
    />
  ) : null;

  const customLinksMenuMaybe = (
    <CustomLinksMenu
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      hasClientSideContentReady={mounted}
      currentPage={currentPage}
      intl={intl}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );

  const signupLinkMaybe = isAuthenticated ? (
    <span
      onClick={() => {
        if (onLogout) {
          try {
            onLogout();
          } catch (e) { }
          history.push('/signup');
        }
      }}
      className={classNames(css.buttonSecondary, css.navBtns, css.signUpBtn)}
      data-testid="topbar-signup-btn"
      style={{ cursor: 'pointer' }}
    >
       <FormattedMessage id="StaticImageSection.joinReThrow" />
    </span>
  ) : (
    <NamedLink name="SignupPage" className={classNames(css.buttonSecondary, css.navBtns, css.signUpBtn)} data-testid="topbar-signup-btn">
       <FormattedMessage id="StaticImageSection.joinReThrow" />
    </NamedLink>
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const desktopSearchButtonMaybe = authenticatedOnClientSide ? (
    <TopbarSearchMenu
      id="desktopSearchButton"
      isAuthenticated={isAuthenticated}
      showSearchForm={showSearchForm}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      isMobile={false}
      appConfig={config}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  ) : null;

  const desktopCartkButtonMaybe = authenticatedOnClientSide ? (
    <Button
      id="desktopCartButton"
      rootClassName={classNames(css.cartMenueBtn, css.iconBtn)}
      title={intl.formatMessage({ id: 'Topbar.cartIcon' })}
    >
      <CartOutlineIcon />
    </Button>
  ) : null;

  const desktopFavouriteButtonMaybe = authenticatedOnClientSide ? (
    <NamedLink
      title={intl.formatMessage({ id: 'Topbar.favouriteIcon' })}
      name="WishlistPage"
      className={classNames(css.favouriteMenueBtn, css.iconBtn)}
      data-testid="topbar-wishlist-btn"
    >
      <div className={css.wishlistIconWrapper}>
        <HeartOutlineIcon />
        {wishlistCount > 0 ? <span className={css.wishlistCount}>{wishlistCount}</span> : null}
      </div>
    </NamedLink>
  ) : null;

  return (
    <div className={css.container}>
      <nav
        className={classes}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.topbarNavigation' })}
      >
        <LinkedLogo
          id="logo-topbar-desktop"
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {/* <div className={css.navLinks} data-testid="topbar-nav-links">
        <NamedLink name="SearchPage" className={css.navLink} data-testid="topbar-browse-link">
          Browse
        </NamedLink>
        <span
          className={css.navLink}
          onClick={() => setIsListingTypeModalOpen(true)}
          role="button"
          tabIndex={0}
          data-testid="topbar-sell-link"
        >
          Sell
        </span>
        <a href="/p/how-it-works" className={css.navLink} data-testid="topbar-how-link">
          How It Works
        </a>
        <a href="/p/community" className={css.navLink} data-testid="topbar-community-link">
          Community
        </a>
      </div> */}

        <div className={css.rightSection}>
          {/* {desktopSearchButtonMaybe} */}
          {/* {inboxLinkMaybe} */}
          {/* {profileMenuMaybe} */}
          {/* {desktopCartkButtonMaybe} */}
          {/* {desktopFavouriteButtonMaybe} */}
          {/* {loginLinkMaybe} */}
          {signupLinkMaybe}
        </div>
        <ListingTypeModal
          isOpen={isListingTypeModalOpen}
          onClose={() => setIsListingTypeModalOpen(false)}
          onManageDisableScrolling={onManageDisableScrolling}
          listingTypes={config.listing.listingTypes}
          onContinue={selectedType => {
            history.push(`/l/new?listingType=${selectedType}`);
          }}
        />
      </nav>
    </div>
  );
};

export default TopbarDesktop;
